"use client";

import React from "react";

interface ReviewsBlockProps {
  storeName: string;
  rating?: number | null;
  ratingCount?: number | null;
  prefix: string;
  heading?: string;
  eyebrow?: string;
}

interface Reason {
  label: string;
  icon: React.ReactNode;
}

const REASONS: Reason[] = [
  {
    label: "Handcrafted in small batches",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 2l2.4 5.3L20 8l-4 4 1 6-5-2.8L7 18l1-6-4-4 5.6-.7L12 2z" />
      </svg>
    ),
  },
  {
    label: "Same-day local delivery",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="1" y="6" width="14" height="11" rx="1.5" />
        <path d="M15 9h4l3 3v5h-7" />
        <circle cx="6" cy="18.5" r="2" />
        <circle cx="18" cy="18.5" r="2" />
      </svg>
    ),
  },
  {
    label: "Easy 7-day returns",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <path d="M3 4v4h4" />
      </svg>
    ),
  },
];

function StarRow({ rating }: { rating: number }) {
  const stars = [0, 1, 2, 3, 4];
  return (
    <div
      className="flex items-center gap-1"
      role="img"
      aria-label={`Rated ${rating.toFixed(1)} out of 5`}
    >
      {stars.map((i) => {
        const fill = Math.max(0, Math.min(1, rating - i));
        return (
          <span key={i} className="relative inline-flex" style={{ width: 20, height: 20 }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--border)"
              strokeWidth="1.5"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 2.5l2.9 6.1 6.6.7-4.9 4.5 1.3 6.5L12 17.8 6.1 20.3l1.3-6.5L2.5 9.3l6.6-.7L12 2.5z" />
            </svg>
            <span
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${fill * 100}%` }}
              aria-hidden="true"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="var(--gold)"
                stroke="var(--gold)"
                strokeWidth="1.5"
                strokeLinejoin="round"
              >
                <path d="M12 2.5l2.9 6.1 6.6.7-4.9 4.5 1.3 6.5L12 17.8 6.1 20.3l1.3-6.5L2.5 9.3l6.6-.7L12 2.5z" />
              </svg>
            </span>
          </span>
        );
      })}
    </div>
  );
}

function VerifiedBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold"
      style={{
        background: "var(--navy-soft)",
        color: "var(--navy)",
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M9 12l2 2 4-4" />
        <path d="M12 2l2.5 1.8 3-.2 1 2.9 2.6 1.6-.9 2.9.9 2.9-2.6 1.6-1 2.9-3-.2L12 22l-2.5-1.8-3 .2-1-2.9L2.9 16l.9-2.9L2.9 10l2.6-1.6 1-2.9 3 .2L12 2z" />
      </svg>
      Verified by GrabLocal
    </span>
  );
}

export const ReviewsBlock: React.FC<ReviewsBlockProps> = ({
  storeName,
  rating,
  ratingCount,
  prefix,
  heading,
  eyebrow,
}) => {
  const hasRating = typeof rating === "number" && rating > 0;
  const hasCount = typeof ratingCount === "number" && ratingCount > 0;

  return (
    <section className="mt-[clamp(40px,5vw,64px)]">
      {/* Kicker */}
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: 18,
            height: 9,
            borderTopLeftRadius: 18,
            borderTopRightRadius: 18,
            background: "var(--coral)",
          }}
        />
        <span
          className="text-[12px] font-semibold uppercase"
          style={{ color: "var(--coral)", letterSpacing: "0.14em" }}
        >
          {eyebrow || "Trusted locally"}
        </span>
      </div>

      <h2
        className="mt-2 text-[clamp(20px,2.6vw,30px)]"
        style={{ fontFamily: "var(--font-display)", fontWeight: 600, color: "var(--techno-black)" }}
      >
        {heading || `Why shop ${storeName}`}
      </h2>

      {/* Card */}
      <div
        className="mt-5 grid grid-cols-1 gap-6 rounded-[var(--radius-xl)] p-6 sm:p-8 md:grid-cols-[minmax(0,0.9fr)_1px_minmax(0,1.1fr)] md:gap-8"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border-soft)",
          boxShadow: "var(--shadow-soft)",
        }}
      >
        {/* LEFT: rating */}
        <div className="flex flex-col items-start justify-center gap-4">
          {hasRating ? (
            <>
              <div className="flex items-end gap-3">
                <span
                  className="leading-none"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 600,
                    fontSize: "clamp(48px,7vw,68px)",
                    color: "var(--techno-black)",
                  }}
                >
                  {rating!.toFixed(1)}
                </span>
                <span
                  className="mb-2 text-[14px]"
                  style={{ color: "var(--techno-black-50)" }}
                >
                  / 5
                </span>
              </div>
              <StarRow rating={rating!} />
              {hasCount && (
                <p className="text-[14px]" style={{ color: "var(--techno-black-70)" }}>
                  {ratingCount} {ratingCount === 1 ? "rating" : "ratings"}
                </p>
              )}
            </>
          ) : (
            <>
              <div
                className="flex items-center justify-center rounded-full"
                style={{
                  width: 56,
                  height: 56,
                  background: "var(--coral-tint)",
                  color: "var(--coral-deep)",
                }}
                aria-hidden="true"
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2.5l2.9 6.1 6.6.7-4.9 4.5 1.3 6.5L12 17.8 6.1 20.3l1.3-6.5L2.5 9.3l6.6-.7L12 2.5z" />
                </svg>
              </div>
              <h3
                className="text-[clamp(18px,2.2vw,22px)]"
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 600,
                  color: "var(--techno-black)",
                }}
              >
                Be among the first to review
              </h3>
              <p className="text-[14px]" style={{ color: "var(--techno-black-70)" }}>
                Every order from {storeName} is fulfilled by a verified local maker.
              </p>
            </>
          )}

          <VerifiedBadge />
        </div>

        {/* Divider */}
        <div
          className="hidden md:block"
          style={{ background: "var(--border-soft)" }}
          aria-hidden="true"
        />

        {/* RIGHT: reasons */}
        <div className="flex flex-col gap-4">
          {REASONS.map((reason) => (
            <div key={reason.label} className="flex items-center gap-3">
              <span
                className="flex shrink-0 items-center justify-center rounded-[var(--radius)]"
                style={{
                  width: 44,
                  height: 44,
                  background: "var(--coral-tint)",
                  color: "var(--coral-deep)",
                }}
                aria-hidden="true"
              >
                {reason.icon}
              </span>
              <span
                className="text-[15px] font-medium"
                style={{ color: "var(--techno-black)" }}
              >
                {reason.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
